import { useState, useCallback, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth0 } from "@auth0/auth0-react";
import {
  getGlobalMediaLibrary,
  getMediaTypeLabel,
  getProcessingStatusDisplay,
  supportsProcessing,
  formatFileSize,
  getMediaInfo,
  MediaItem,
  MediaLibraryFilters,
  MEDIA_TYPE_OPTIONS,
  checkFilenames,
  FilenameConflict,
} from "../api/media";
import { deleteMedia, uploadMedia, TreeSummary } from "../api/trees";
import { listTrees } from "../api/trees";
import {
  uploadSnippet,
  SnippetItem,
  isValidSnippetImageType,
} from "../api/snippets";
import LoadingSpinner from "../components/Common/LoadingSpinner";
import { MediaImage, MediaDetailModal, UploadConflictModal } from "../components/Media";
import { SnippetWizard } from "../components/Snippet";
import { useVaultContext } from "../context/VaultContext";

type ViewMode = "grid" | "list";

export default function GlobalMediaLibrary() {
  const { getAccessTokenSilently } = useAuth0();
  const queryClient = useQueryClient();
  const { currentVaultId } = useVaultContext();

  // State
  const [viewMode, setViewMode] = useState<ViewMode>("grid");
  const [filters, setFilters] = useState<MediaLibraryFilters>({
    page: 1,
    pageSize: 20,
    vaultId: currentVaultId || undefined,
  });
  const [selectedMedia, setSelectedMedia] = useState<MediaItem | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadTreeId, setUploadTreeId] = useState<number | null>(null);
  const [uploadMediaType, setUploadMediaType] = useState<string>(""); // empty = auto-detect
  
  // Conflict resolution state
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [fileConflicts, setFileConflicts] = useState<FilenameConflict[]>([]);
  const [showConflictModal, setShowConflictModal] = useState(false);

  // Snippet wizard state
  const [activeSnippet, setActiveSnippet] = useState<SnippetItem | null>(null);
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const [snippetUploadError, setSnippetUploadError] = useState<string | null>(null);
  const dropZoneRef = useRef<HTMLDivElement>(null);

  // Update filters when vault changes
  useEffect(() => {
    setFilters(prev => ({ ...prev, vaultId: currentVaultId || undefined, page: 1 }));
  }, [currentVaultId]);

  // Fetch trees for upload selection (filtered by current vault)
  const { data: trees } = useQuery({
    queryKey: ["trees", currentVaultId],
    queryFn: async () => {
      const token = await getAccessTokenSilently();
      return listTrees(token, currentVaultId || undefined);
    },
    enabled: !!currentVaultId,
  });

  // Fetch global media library
  const { data: libraryData, isLoading, error } = useQuery({
    queryKey: ["globalMediaLibrary", filters],
    queryFn: async () => {
      const token = await getAccessTokenSilently();
      return getGlobalMediaLibrary(token, filters);
    },
    enabled: !!currentVaultId,
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (mediaId: number) => {
      const token = await getAccessTokenSilently();
      return deleteMedia(mediaId, token);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["globalMediaLibrary"] });
      setSelectedMedia(null);
    },
  });

  // Refresh media callback - called after modal processes a file
  const refreshSelectedMedia = useCallback(async () => {
    queryClient.invalidateQueries({ queryKey: ["globalMediaLibrary"] });
    if (selectedMedia) {
      try {
        const token = await getAccessTokenSilently();
        const updated = await getMediaInfo(selectedMedia.id, token);
        setSelectedMedia(updated);
      } catch (err) {
        console.error("Failed to refresh media:", err);
      }
    }
  }, [selectedMedia, getAccessTokenSilently, queryClient]);

  // File upload handler - checks for conflicts first (per-vault)
  const handleFileUpload = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    if (!currentVaultId) {
      setUploadError("No vault selected. Please select a vault first.");
      return;
    }

    const fileArray = Array.from(files);
    setUploadError(null);
    
    // Reset the input immediately so the same files can be selected again if needed
    e.target.value = "";

    try {
      const token = await getAccessTokenSilently();
      const filenames = fileArray.map(f => f.name);
      const result = await checkFilenames(currentVaultId, filenames, token);

      if (result.conflicts.length > 0) {
        // Show conflict modal
        setPendingFiles(fileArray);
        setFileConflicts(result.conflicts);
        setShowConflictModal(true);
      } else {
        // No conflicts - upload directly
        await performUpload(fileArray.map(f => ({ file: f, filename: f.name })));
      }
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : "Failed to check files");
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentVaultId, getAccessTokenSilently]);

  // Perform the actual upload after conflict resolution
  const performUpload = useCallback(async (filesToUpload: Array<{ file: File; filename: string }>) => {
    if (filesToUpload.length === 0) return;
    if (!currentVaultId) {
      setUploadError("No vault selected. Please select a vault first.");
      return;
    }

    setIsUploading(true);
    setUploadError(null);

    try {
      const token = await getAccessTokenSilently();
      for (const { file, filename } of filesToUpload) {
        // Create a new File object with the potentially renamed filename
        const uploadFile = filename !== file.name 
          ? new File([file], filename, { type: file.type })
          : file;
        
        await uploadMedia(uploadFile, uploadTreeId ?? 0, token, {
          vaultId: currentVaultId ?? undefined,
          mediaTypeOverride: uploadMediaType || undefined,
        });
      }
      queryClient.invalidateQueries({ queryKey: ["globalMediaLibrary"] });
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : "Upload failed");
    } finally {
      setIsUploading(false);
    }
  }, [currentVaultId, uploadTreeId, uploadMediaType, getAccessTokenSilently, queryClient]);

  // Handle confirmed upload from conflict modal
  const handleConfirmedUpload = useCallback((filesToUpload: Array<{ file: File; filename: string }>) => {
    setShowConflictModal(false);
    setPendingFiles([]);
    setFileConflicts([]);
    performUpload(filesToUpload);
  }, [performUpload]);

  // Handle cancel from conflict modal
  const handleCancelUpload = useCallback(() => {
    setShowConflictModal(false);
    setPendingFiles([]);
    setFileConflicts([]);
  }, []);

  // ==========================================================================
  // Snippet Capture (Paste/Drop)
  // ==========================================================================

  // Handle snippet creation from paste or drop
  const handleSnippetCapture = useCallback(async (imageData: Blob | File) => {
    if (!isValidSnippetImageType(imageData)) {
      setSnippetUploadError("Only image files are supported for snippets");
      setTimeout(() => setSnippetUploadError(null), 5000);
      return;
    }

    try {
      const token = await getAccessTokenSilently();
      const snippet = await uploadSnippet(imageData, token);
      setActiveSnippet(snippet);
      setSnippetUploadError(null);
    } catch (err) {
      setSnippetUploadError(err instanceof Error ? err.message : "Failed to create snippet");
      setTimeout(() => setSnippetUploadError(null), 5000);
    }
  }, [getAccessTokenSilently]);

  // Handle clipboard paste
  useEffect(() => {
    const handlePaste = (e: ClipboardEvent) => {
      // Don't capture paste if user is typing in an input
      if (
        document.activeElement?.tagName === "INPUT" ||
        document.activeElement?.tagName === "TEXTAREA"
      ) {
        return;
      }

      const items = e.clipboardData?.items;
      if (!items) return;

      for (const item of items) {
        if (item.type.startsWith("image/")) {
          e.preventDefault();
          const blob = item.getAsFile();
          if (blob) {
            handleSnippetCapture(blob);
          }
          break;
        }
      }
    };

    document.addEventListener("paste", handlePaste);
    return () => document.removeEventListener("paste", handlePaste);
  }, [handleSnippetCapture]);

  // Handle drag and drop
  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Check if it's a file drag
    if (e.dataTransfer.types.includes("Files")) {
      setIsDraggingOver(true);
    }
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Only set false if leaving the drop zone entirely
    const rect = dropZoneRef.current?.getBoundingClientRect();
    if (rect) {
      const { clientX, clientY } = e;
      if (
        clientX < rect.left ||
        clientX > rect.right ||
        clientY < rect.top ||
        clientY > rect.bottom
      ) {
        setIsDraggingOver(false);
      }
    }
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingOver(false);

    const files = e.dataTransfer.files;
    if (files.length === 0) return;

    // For snippet wizard, only accept single image file
    const file = files[0];
    if (file.type.startsWith("image/")) {
      handleSnippetCapture(file);
    } else {
      setSnippetUploadError("Only image files are supported for snippets");
      setTimeout(() => setSnippetUploadError(null), 5000);
    }
  }, [handleSnippetCapture]);

  // Handle snippet wizard close
  const handleSnippetWizardClose = useCallback(() => {
    setActiveSnippet(null);
  }, []);

  // Handle snippet wizard complete
  const handleSnippetComplete = useCallback(() => {
    setActiveSnippet(null);
    queryClient.invalidateQueries({ queryKey: ["globalMediaLibrary"] });
  }, [queryClient]);

  // Filter handlers
  const handleFilterChange = (key: keyof MediaLibraryFilters, value: string | number | undefined) => {
    setFilters(prev => ({
      ...prev,
      [key]: value || undefined,
      page: key === "page" ? (value as number) : 1, // Reset page when other filters change
    }));
  };

  return (
    <div 
      ref={dropZoneRef}
      className={`min-h-screen bg-vault-50 relative ${isDraggingOver ? 'ring-4 ring-inset ring-vault-500' : ''}`}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      {/* Drop zone overlay */}
      {isDraggingOver && (
        <div className="fixed inset-0 z-40 bg-vault-900/50 flex items-center justify-center pointer-events-none">
          <div className="bg-white rounded-xl p-8 shadow-2xl text-center">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-16 h-16 mx-auto text-vault-600 mb-4">
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
            </svg>
            <h3 className="text-xl font-semibold text-vault-900 mb-1">Drop image to create snippet</h3>
            <p className="text-vault-600">Release to start the snippet wizard</p>
          </div>
        </div>
      )}

      {/* Snippet error toast */}
      {snippetUploadError && (
        <div className="fixed bottom-4 right-4 z-50 bg-red-100 border border-red-300 text-red-700 px-4 py-3 rounded-lg shadow-lg">
          {snippetUploadError}
        </div>
      )}

      {/* Snippet Wizard Modal */}
      {activeSnippet && (
        <SnippetWizard
          snippet={activeSnippet}
          onClose={handleSnippetWizardClose}
          onComplete={handleSnippetComplete}
        />
      )}

      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="mx-auto max-w-7xl px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2 text-sm text-vault-500">
                <Link to="/dashboard" className="hover:text-vault-700">Dashboard</Link>
                <span>/</span>
                <span className="text-vault-700">Media Library</span>
              </div>
              <h1 className="mt-1 text-2xl font-bold text-vault-900">Media Library</h1>
              <p className="text-sm text-vault-500">All uploaded media across your family vault</p>
            </div>
            <div className="flex items-center gap-4">
              <Link
                to="/dashboard"
                className="rounded-lg border border-vault-300 px-4 py-2 text-sm text-vault-600 hover:bg-vault-50"
              >
                Back to Dashboard
              </Link>
              <div className="flex items-center gap-2">
                {/* Create Snippet button */}
                <div className="relative group">
                  <button
                    className="rounded-lg border border-vault-300 px-4 py-2 text-sm font-medium text-vault-600 hover:bg-vault-50 flex items-center gap-2"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 7.5h-.75A2.25 2.25 0 004.5 9.75v7.5a2.25 2.25 0 002.25 2.25h7.5a2.25 2.25 0 002.25-2.25v-7.5a2.25 2.25 0 00-2.25-2.25h-.75m0-3l-3-3m0 0l-3 3m3-3v11.25m6-2.25h.75a2.25 2.25 0 012.25 2.25v7.5a2.25 2.25 0 01-2.25 2.25h-7.5a2.25 2.25 0 01-2.25-2.25v-.75" />
                    </svg>
                    Create Snippet
                  </button>
                  {/* Tooltip */}
                  <div className="absolute top-full left-0 mt-2 w-64 bg-vault-800 text-white text-xs rounded-lg p-3 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50 shadow-lg">
                    <p className="font-semibold mb-1">📋 To create a snippet:</p>
                    <ul className="space-y-1 ml-4 list-disc">
                      <li>Press <kbd className="px-1 py-0.5 bg-vault-700 rounded">Ctrl+V</kbd> to paste</li>
                      <li>Or drag & drop an image here</li>
                    </ul>
                  </div>
                </div>
                {/* Tree selector for upload */}
                <select
                  className="rounded-lg border border-vault-200 px-3 py-2 text-sm"
                  value={uploadTreeId || ""}
                  onChange={(e) => setUploadTreeId(e.target.value ? Number(e.target.value) : null)}
                  title="Associate with tree"
                >
                  <option value="">No tree (global)</option>
                  {trees?.map((tree: TreeSummary) => (
                    <option key={tree.id} value={tree.id}>{tree.name}</option>
                  ))}
                </select>
                {/* Media type override for upload */}
                <select
                  className="rounded-lg border border-vault-200 px-3 py-2 text-sm"
                  value={uploadMediaType}
                  onChange={(e) => setUploadMediaType(e.target.value)}
                  title="Set media type (or auto-detect)"
                >
                  <option value="">Auto-detect type</option>
                  {MEDIA_TYPE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                <label className="cursor-pointer rounded-lg bg-vault-600 px-4 py-2 text-sm font-medium text-white hover:bg-vault-700">
                  {isUploading ? "Uploading..." : "Upload Media"}
                  <input
                    type="file"
                    multiple
                    accept="image/*,audio/*,video/*,.pdf,.doc,.docx"
                    className="hidden"
                    onChange={handleFileUpload}
                    disabled={isUploading}
                  />
                </label>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
        {/* Filters */}
        <div className="mb-6 rounded-lg bg-white p-4 shadow-sm">
          <div className="flex flex-wrap items-center gap-4">
            {/* Search */}
            <div className="flex-1 min-w-[200px]">
              <input
                type="text"
                placeholder="Search caption, transcription, OCR text..."
                className="w-full rounded-lg border border-vault-200 px-3 py-2 text-sm"
                value={filters.search || ""}
                onChange={(e) => handleFilterChange("search", e.target.value)}
              />
            </div>

            {/* Media Type Filter */}
            <select
              className="rounded-lg border border-vault-200 px-3 py-2 text-sm"
              value={filters.mediaType || ""}
              onChange={(e) => handleFilterChange("mediaType", e.target.value)}
            >
              <option value="">All Types</option>
              <option value="photo">Photos</option>
              <option value="document">Documents</option>
              <option value="audio">Audio</option>
              <option value="video">Video</option>
            </select>

            {/* Processing Status Filter */}
            <select
              className="rounded-lg border border-vault-200 px-3 py-2 text-sm"
              value={filters.processingStatus || ""}
              onChange={(e) => handleFilterChange("processingStatus", e.target.value)}
            >
              <option value="">All Status</option>
              <option value="pending">Pending</option>
              <option value="processing">Processing</option>
              <option value="completed">Completed</option>
              <option value="failed">Failed</option>
            </select>

            {/* View Mode Toggle */}
            <div className="flex rounded-lg border border-vault-200 p-1">
              <button
                className={`px-3 py-1 rounded text-sm ${viewMode === "grid" ? "bg-vault-600 text-white" : "text-vault-600"}`}
                onClick={() => setViewMode("grid")}
              >
                Grid
              </button>
              <button
                className={`px-3 py-1 rounded text-sm ${viewMode === "list" ? "bg-vault-600 text-white" : "text-vault-600"}`}
                onClick={() => setViewMode("list")}
              >
                List
              </button>
            </div>
          </div>
        </div>

        {/* Upload Error */}
        {uploadError && (
          <div className="mb-4 rounded-lg bg-red-50 p-4 text-red-700">
            <p>{uploadError}</p>
            <button
              onClick={() => setUploadError(null)}
              className="mt-2 text-sm underline hover:no-underline"
            >
              Dismiss
            </button>
          </div>
        )}

        {/* Loading State */}
        {isLoading && (
          <div className="flex items-center justify-center py-12">
            <LoadingSpinner size="lg" />
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="rounded-lg bg-red-50 p-4 text-red-700">
            Failed to load media library. Please try again.
          </div>
        )}

        {/* Media Grid/List */}
        {libraryData && (
          <>
            {libraryData.items.length === 0 ? (
              <div className="rounded-lg border-2 border-dashed border-vault-200 p-12 text-center">
                <p className="text-vault-500">No media found</p>
                <p className="mt-2 text-sm text-vault-400">
                  Upload some photos, documents, or recordings to get started.
                </p>
              </div>
            ) : viewMode === "grid" ? (
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
                {libraryData.items.map((media) => (
                  <MediaCard
                    key={media.id}
                    media={media}
                    onClick={() => setSelectedMedia(media)}
                  />
                ))}
              </div>
            ) : (
              <div className="space-y-2">
                {libraryData.items.map((media) => (
                  <MediaListItem
                    key={media.id}
                    media={media}
                    onClick={() => setSelectedMedia(media)}
                  />
                ))}
              </div>
            )}

            {/* Pagination */}
            {libraryData.total_pages > 1 && (
              <div className="mt-6 flex items-center justify-center gap-2">
                <button
                  className="rounded-lg border border-vault-200 px-3 py-2 text-sm disabled:opacity-50"
                  disabled={filters.page === 1}
                  onClick={() => handleFilterChange("page", (filters.page || 1) - 1)}
                >
                  Previous
                </button>
                <span className="text-sm text-vault-600">
                  Page {libraryData.page} of {libraryData.total_pages}
                </span>
                <button
                  className="rounded-lg border border-vault-200 px-3 py-2 text-sm disabled:opacity-50"
                  disabled={libraryData.page >= libraryData.total_pages}
                  onClick={() => handleFilterChange("page", (filters.page || 1) + 1)}
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}

        {/* Media Detail Modal */}
        {selectedMedia && (
          <MediaDetailModal
            mode="global"
            media={selectedMedia}
            trees={trees || []}
            onClose={() => setSelectedMedia(null)}
            onDelete={() => deleteMutation.mutate(selectedMedia.id)}
            onProcess={refreshSelectedMedia}
            onMediaUpdated={refreshSelectedMedia}
          />
        )}

        {/* Upload Conflict Modal */}
        {showConflictModal && (
          <UploadConflictModal
            files={pendingFiles}
            conflicts={fileConflicts}
            onConfirm={handleConfirmedUpload}
            onCancel={handleCancelUpload}
          />
        )}
      </main>
    </div>
  );
}

// =============================================================================
// SUB-COMPONENTS
// =============================================================================

interface MediaCardProps {
  media: MediaItem;
  onClick: () => void;
}

function MediaCard({ media, onClick }: MediaCardProps) {
  const isImage = media.media_type === "photo";
  const status = getProcessingStatusDisplay(media.processing_status);

  return (
    <div
      className="group relative cursor-pointer overflow-hidden rounded-lg bg-white shadow-sm transition-shadow hover:shadow-md"
      onClick={onClick}
    >
      {/* Thumbnail */}
      <div className="aspect-square bg-vault-100">
        {isImage ? (
          <MediaImage
            mediaId={media.id}
            alt={media.caption || media.original_filename}
            className="h-full w-full object-cover"
            cacheBuster={media.file_size}
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center">
            <span className="text-4xl text-vault-400">
              {media.media_type === "document" ? "📄" : 
               media.media_type === "audio" ? "🎵" : 
               media.media_type === "video" ? "🎬" : "📁"}
            </span>
          </div>
        )}
      </div>

      {/* Info */}
      <div className="p-2">
        <p className="truncate text-sm font-medium text-vault-900">
          {media.caption || media.original_filename}
        </p>
        <div className="mt-1 flex items-center justify-between text-xs text-vault-500">
          <span>{getMediaTypeLabel(media.media_type)}</span>
          {media.tags.length > 0 && (
            <span className="rounded bg-vault-100 px-1">
              {media.tags.length} tagged
            </span>
          )}
        </div>
      </div>

      {/* Processing Status Badge */}
      {supportsProcessing(media.media_type) && media.processing_status !== "skipped" && (
        <div className={`absolute top-2 right-2 rounded px-2 py-0.5 text-xs font-medium bg-${status.color}-100 text-${status.color}-700`}>
          {status.label}
        </div>
      )}
    </div>
  );
}

interface MediaListItemProps {
  media: MediaItem;
  onClick: () => void;
}

function MediaListItem({ media, onClick }: MediaListItemProps) {
  const status = getProcessingStatusDisplay(media.processing_status);

  return (
    <div
      className="flex cursor-pointer items-center gap-4 rounded-lg bg-white p-4 shadow-sm hover:shadow-md"
      onClick={onClick}
    >
      {/* Thumbnail */}
      <div className="h-16 w-16 flex-shrink-0 overflow-hidden rounded bg-vault-100">
        {media.media_type === "photo" ? (
          <MediaImage
            mediaId={media.id}
            alt={media.original_filename}
            className="h-full w-full object-cover"
            cacheBuster={media.file_size}
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-2xl">
            {media.media_type === "document" ? "📄" : 
             media.media_type === "audio" ? "🎵" : 
             media.media_type === "video" ? "🎬" : "📁"}
          </div>
        )}
      </div>

      {/* Info */}
      <div className="flex-1 min-w-0">
        <p className="truncate font-medium text-vault-900">
          {media.caption || media.original_filename}
        </p>
        <p className="text-sm text-vault-500">
          {getMediaTypeLabel(media.media_type)} • {formatFileSize(media.file_size)}
          {media.date_taken && ` • ${media.date_taken}`}
        </p>
        {media.tags.length > 0 && (
          <p className="mt-1 text-sm text-vault-600">
            Tagged: {media.tags.map(t => t.person_name).filter(Boolean).join(", ")}
          </p>
        )}
      </div>

      {/* Status */}
      {supportsProcessing(media.media_type) && (
        <div className={`rounded px-2 py-1 text-xs font-medium bg-${status.color}-100 text-${status.color}-700`}>
          {status.label}
        </div>
      )}
    </div>
  );
}
